import { Text, TextProps } from '@chakra-ui/react';
import React, { useState } from 'react';

interface Props extends Omit<TextProps, 'children'> {
  text: string;
  maxLength?: number;
  showMoreLabel?: string;
  showLessLabel?: string;
}

// Truncates at the last whitespace before `maxLength` so we don't cut a word mid-way.
const truncate = (input: string, maxLength: number): string => {
  if (input.length <= maxLength) return input;
  const sliced = input.slice(0, maxLength);
  const lastSpace = sliced.lastIndexOf(' ');
  const head = lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced;
  return `${head.trimEnd()}…`;
};

/**
 * Renders `text` clamped to ~`maxLength` characters with an inline "READ MORE" / "READ LESS"
 * toggle appended at the end of the same paragraph (flows with the body copy). When the text
 * is shorter than the limit it renders as a plain `<Text>` — no toggle, no extra DOM.
 * Forwards any `TextProps` to the body `<Text>` so callers control font / colour / weight.
 */
const ExpandableText = ({
  text,
  maxLength = 120,
  showMoreLabel = 'READ MORE',
  showLessLabel = 'READ LESS',
  ...textProps
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = text.length > maxLength;

  if (!needsTruncation) {
    return <Text {...textProps}>{text}</Text>;
  }

  const display = expanded ? text : truncate(text, maxLength);

  return (
    <Text {...textProps}>
      {display}{' '}
      <Text
        as={'span'}
        variant={'urbanistMaximumBold10'}
        color={'text.mediumBlue'}
        cursor={'pointer'}
        role={'button'}
        tabIndex={0}
        onClick={(event) => {
          event.stopPropagation();
          setExpanded((prev) => !prev);
        }}
        _hover={{ opacity: 0.8 }}
        whiteSpace={'nowrap'}
      >
        {expanded ? showLessLabel : showMoreLabel}
      </Text>
    </Text>
  );
};

export default ExpandableText;
